import { useEffect, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "@/hooks/use-theme";
import { AuthProvider } from "@/hooks/use-auth";
import { NativeBootstrap } from "@/hooks/use-native-platform";
import { LanguageProvider } from "@/hooks/use-language";
import { PresenceProvider } from "@/hooks/use-presence";
import { UserLocationProvider } from "@/hooks/use-user-location";
import { CurrencyProvider } from "@/hooks/use-currency";
import { toast } from "sonner";
import Index from "./pages/Index";
import { lazyWithRetry as lazy } from "@/lib/lazy-with-retry";
const NotFound = lazy(() => import("./pages/NotFound"));
const About = lazy(() => import("./pages/About"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const Pricing = lazy(() => import("./pages/Pricing"));
const ForProfessionals = lazy(() => import("./pages/ForProfessionals"));
const Contact = lazy(() => import("./pages/Contact"));
const HelpCenter = lazy(() => import("./pages/HelpCenter"));
const Safety = lazy(() => import("./pages/Safety"));
const Trust = lazy(() => import("./pages/Trust"));
const BrowseServices = lazy(() => import("./pages/BrowseServices"));
const Auth = lazy(() => import("./pages/Auth"));
const Chat = lazy(() => import("./pages/Chat"));
const ProviderProfile = lazy(() => import("./pages/ProviderProfile"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const ProfileEditor = lazy(() => import("./pages/ProfileEditor"));
const Wallet = lazy(() => import("./pages/Wallet"));
const WalletHistory = lazy(() => import("./pages/WalletHistory"));
const Notifications = lazy(() => import("./pages/Notifications"));
const Terms = lazy(() => import("./pages/legal/Terms"));
const Privacy = lazy(() => import("./pages/legal/Privacy"));
const Cookies = lazy(() => import("./pages/legal/Cookies"));
const Refund = lazy(() => import("./pages/legal/Refund"));
const HelpArticle = lazy(() => import("./pages/HelpArticle"));
const Bookmarks = lazy(() => import("./pages/Bookmarks"));
const Invoices = lazy(() => import("./pages/Invoices"));
const GuidesIndex = lazy(() => import("./pages/GuidesIndex"));
const CityServiceGuide = lazy(() => import("./pages/CityServiceGuide"));
const JobBroadcasts = lazy(() => import("./pages/JobBroadcasts"));
const JobBroadcastNew = lazy(() => import("./pages/JobBroadcastNew"));
const JobBroadcastDetail = lazy(() => import("./pages/JobBroadcastDetail"));
const InvitationDetail = lazy(() => import("./pages/InvitationDetail"));
const BroadcastReceipt = lazy(() => import("./pages/BroadcastReceipt"));
const JobBillingHistory = lazy(() => import("./pages/JobBillingHistory"));
const PublicJobInvite = lazy(() => import("./pages/PublicJobInvite"));
const Moderation = lazy(() => import("./pages/admin/Moderation"));
const PhoneUpload = lazy(() => import("./pages/PhoneUpload"));
const Advertise = lazy(() => import("./pages/Advertise"));
import { LocationPrompt } from "@/components/LocationPrompt";
import { CookieConsent } from "@/components/CookieConsent";
import { GlobalMessageSound } from "@/components/GlobalMessageSound";
import { BookmarkPresenceNotifier } from "@/components/BookmarkPresenceNotifier";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const App = () => {
  // Global error handler for unhandled promise rejections
  useEffect(() => {
    const handleRejection = (event: PromiseRejectionEvent) => {
      console.error("Unhandled rejection:", event.reason);
      toast.error("An error occurred. Please try again.");
      event.preventDefault();
    };

    const handleError = (event: ErrorEvent) => {
      console.error("Unhandled error:", event.error);
      event.preventDefault();
    };

    window.addEventListener("unhandledrejection", handleRejection);
    window.addEventListener("error", handleError);
    
    return () => {
      window.removeEventListener("unhandledrejection", handleRejection);
      window.removeEventListener("error", handleError);
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <ThemeProvider defaultTheme="system" storageKey="taskratel-ui-theme">
          <AuthProvider>
            <PresenceProvider>
              <UserLocationProvider>
              <CurrencyProvider>
              <TooltipProvider>
                <Toaster />
                <NativeBootstrap />
                <Sonner />
                <BrowserRouter>
                  <LocationPrompt />
                  <CookieConsent />
                  <GlobalMessageSound />
                  <BookmarkPresenceNotifier />
                  <Suspense
                    fallback={
                      <div className="flex min-h-dvh items-center justify-center text-muted-foreground">
                        Loading…
                      </div>
                    }
                  >
                  <Routes>
                    <Route path="/" element={<Index />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/how-it-works" element={<HowItWorks />} />
                    <Route path="/pricing" element={<Pricing />} />
                    <Route path="/advertise" element={<Advertise />} />
                    <Route path="/for-professionals" element={<ForProfessionals />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/help" element={<HelpCenter />} />
                    <Route path="/help/:slug" element={<HelpArticle />} />
                    <Route path="/safety" element={<Safety />} />
                    <Route path="/trust" element={<Trust />} />
                    <Route path="/browse" element={<BrowseServices />} />
                    <Route path="/auth" element={<Auth />} />
                    <Route path="/chat" element={<Chat />} />
                    <Route path="/chat/:conversationId" element={<Chat />} />
                    <Route path="/provider/:providerId" element={<ProviderProfile />} />
                    <Route path="/profile" element={<ProfileEditor />} />
                    <Route path="/wallet" element={<Wallet />} />
                    <Route path="/wallet/history" element={<WalletHistory />} />
                    <Route path="/notifications" element={<Notifications />} />
                    <Route path="/admin" element={<AdminDashboard />} />
                    <Route path="/bookmarks" element={<Bookmarks />} />
                    <Route path="/invoices" element={<Invoices />} />
                    <Route path="/guides" element={<GuidesIndex />} />
                    <Route path="/guides/:city/:service" element={<CityServiceGuide />} />
                    <Route path="/jobs" element={<JobBroadcasts />} />
                    <Route path="/jobs/new" element={<JobBroadcastNew />} />
                    <Route path="/jobs/billing" element={<JobBillingHistory />} />
                    <Route path="/jobs/:id" element={<JobBroadcastDetail />} />
                    <Route path="/jobs/:id/receipt" element={<BroadcastReceipt />} />
                    <Route path="/invitations/:id" element={<InvitationDetail />} />
                    <Route path="/j/:slug" element={<PublicJobInvite />} />
                    <Route path="/admin/moderation" element={<Moderation />} />
                    <Route path="/upload-link/:sessionId" element={<PhoneUpload />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/cookies" element={<Cookies />} />
                    <Route path="/refund" element={<Refund />} />
                    {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                  </Suspense>
                </BrowserRouter>
              </TooltipProvider>
              </CurrencyProvider>
              </UserLocationProvider>
            </PresenceProvider>
          </AuthProvider>
        </ThemeProvider>
      </LanguageProvider>
    </QueryClientProvider>
  );
};

export default App;
